import { memo } from 'react';
import { Handle, NodeResizer, Position, type NodeProps } from '@xyflow/react';
import {
  kindLabel,
  layerLabel,
  MAP_CONNECT_SOURCE_HANDLE,
  MAP_CONNECT_TARGET_HANDLE,
  type MapAnchorSide,
  type MapNodeKind,
  type MapNodeLayer,
} from '../../lib/systemMap';
import { useSystemMapNodeActions } from './SystemMapNodeActionsContext';
import { anchorSideToPosition, handleStyleForAnchor } from './systemMapNodeGeometry';

export type SystemMapConnectionHandle = {
  id: string;
  type: 'source' | 'target';
  side: MapAnchorSide;
  offset: number;
  active: boolean;
};

export type SystemMapNodeData = {
  label: string;
  slug: string;
  kind: MapNodeKind;
  layer: MapNodeLayer;
  showThumbnail: boolean;
  screenshotUrl: string | null;
  width: number;
  height: number;
  highlighted: boolean;
  dimmed: boolean;
  connectMode: boolean;
  isConnectSource: boolean;
  connectionHandles: SystemMapConnectionHandle[];
};

const KIND_STYLES: Record<MapNodeKind, string> = {
  workflow: 'border-sky-400 dark:border-sky-500 bg-sky-50 dark:bg-sky-950/40',
  content: 'border-violet-400 dark:border-violet-500 bg-violet-50 dark:bg-violet-950/40',
  site: 'border-rose-400 dark:border-rose-500 bg-rose-50 dark:bg-rose-950/40',
  infra: 'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-950/40',
  integration: 'border-orange-400 dark:border-orange-500 bg-orange-50 dark:bg-orange-950/40',
  repo: 'border-emerald-400 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40',
};

function SystemMapNode({ id, data, selected }: NodeProps) {
  const nodeData = data as SystemMapNodeData;
  const { onResizeEnd } = useSystemMapNodeActions();
  const hasThumbnail = nodeData.showThumbnail && Boolean(nodeData.screenshotUrl);
  const highlightRing = nodeData.isConnectSource
    ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
    : nodeData.highlighted
    ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
    : selected
      ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
      : '';

  return (
    <>
      <NodeResizer
        isVisible={selected && !nodeData.connectMode}
        minWidth={120}
        minHeight={56}
        maxWidth={520}
        maxHeight={420}
        lineClassName="!border-emerald-500"
        handleClassName="!w-2 !h-2 !bg-emerald-500 !border-white"
        onResizeEnd={(_event, params) => {
          onResizeEnd(id, params.width, params.height);
        }}
      />
      <div
        className={`w-full h-full box-border rounded-lg border-2 shadow-sm flex flex-col overflow-hidden transition-opacity ${hasThumbnail ? '' : 'px-3 py-2'} ${KIND_STYLES[nodeData.kind]} ${highlightRing} ${nodeData.dimmed ? 'opacity-35' : 'opacity-100'}`}
      >
        {nodeData.connectionHandles.map((handle) => (
          <Handle
            key={handle.id}
            id={handle.id}
            type={handle.type}
            position={anchorSideToPosition(handle.side)}
            style={handleStyleForAnchor(handle.side, handle.offset)}
            className={`!w-2.5 !h-2.5 !border-2 !border-white ${
              handle.active ? '!bg-sky-500 !scale-125' : '!bg-gray-400'
            }`}
          />
        ))}
        {nodeData.connectMode ? (
          <>
            <Handle
              id={MAP_CONNECT_SOURCE_HANDLE}
              type="source"
              position={Position.Right}
              style={{ top: '50%', transform: 'translate(50%, -50%)' }}
              className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white !opacity-100"
            />
            <Handle
              id={MAP_CONNECT_TARGET_HANDLE}
              type="target"
              position={Position.Left}
              style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
              className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white !opacity-100"
            />
          </>
        ) : null}
        {hasThumbnail ? (
          <img
            src={nodeData.screenshotUrl!}
            alt=""
            className="w-full flex-1 min-h-12 object-cover object-top"
          />
        ) : null}
        <div className={`shrink-0 ${hasThumbnail ? 'px-3 py-2' : ''}`}>
          <div className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
            {nodeData.label}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            <span className="text-[10px] uppercase tracking-wide font-medium text-gray-600 dark:text-gray-300">
              {kindLabel(nodeData.kind)}
            </span>
            <span className="text-[10px] text-gray-400">·</span>
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {layerLabel(nodeData.layer)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

export default memo(SystemMapNode);
